import { propagation, context } from '@opentelemetry/api'
import { logger } from '@/utils/logger'

// Class to add tracing and traceparent headers to XHR requests
// Saves original XMLHttpRequest.open/send() functions, try to start/end span, and then use and restore original function
class XHRTracer {
  constructor() {
    this.ignoredUrls = [
      'https://api-js.mixpanel.com',
      import.meta.env.VITE_FARO_URL,
      'https://identitytoolkit.googleapis.com/'
    ]
    this.stateLabels = {
      0: 'UNSENT',
      1: 'OPENED',
      2: 'HEADERS_RECEIVED',
      3: 'LOADING',
      4: 'DONE'
    }
    this.__currentContext = null
    this.__currentSpan = null

    this.originalOpen = XMLHttpRequest.prototype.open
    this.originalSend = XMLHttpRequest.prototype.send
  }

  setContext(context, span) {
    this.__currentContext = context
    this.__currentSpan = span
  }

  clearContext() {
    this.__currentContext = null
    this.__currentSpan = null
  }

  getActiveContext() {
    return this.__currentContext || context.active()
  }

  getActiveSpan() {
    return this.__currentSpan || this.__currentContext?.span
  }

  // Function to check if the URL should be ignored
  shouldIgnore(url) {
    // Add guard clause to ensure url is a string
    if (!url || typeof url !== 'string') {
      return false
    }
    return this.ignoredUrls.some((ignored) => url.startsWith(ignored))
  }

  // Function to normalize pathname and extract boardid
  normalizePath(pathname) {
    const boardIdMatch = pathname.match(/\/(bd_[a-zA-Z0-9]+)/)
    const boardId = boardIdMatch ? boardIdMatch[1] : null
    const normalized = pathname.replace(/\/bd_[a-zA-Z0-9]+/g, '/:boardid')
    return { normalized, boardId }
  }

  init(faro) {
    const self = this

    // Patching XMLHttpRequest.open() to add tracing and traceparent headers
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__otel_metadata = { method, url }

      if (self.shouldIgnore(url)) {
        this.__otel_skip = true
        return self.originalOpen.apply(this, arguments)
      }

      const otelAPI = faro?.api?.getOTEL()
      const traceAPI = otelAPI?.trace

      let activeContext = self.getActiveContext()
      const tracer = traceAPI.getTracer('xhr')

      let normalized = url
      let boardId = 'none'
      try {
        const urlPath = new URL(url, window.location.origin)
        const normalizedResult = self.normalizePath(urlPath.pathname)
        normalized = normalizedResult.normalized
        boardId = normalizedResult.boardId || 'none'
      } catch (err) {
        logger.warn('⚠️ Failed to parse or normalize URL:', url, err.message)
      }

      const span = tracer.startSpan(
        `${method} ${normalized}`,
        {
          kind: 2,
          attributes: {
            'http.method': method || 'GET',
            'http.url': url || '',
            boardid: boardId,
            service: 'agentnoon-frontend',
            'service.name': 'agentnoon-frontend',
            ...(self.getActiveSpan()?.spanContext() && {
              'navigation.parentSpanId': self.getActiveSpan().spanContext().spanId
            }),
            'navigation.type': 'xhr'
          }
        },
        activeContext
      )

      this.__otel_span = span
      let contextToUse = traceAPI.setSpan(activeContext, span)
      const headers = {}
      propagation.inject(contextToUse, headers)
      if (headers.traceparent) {
        headers['x-custom-traceparent'] = headers.traceparent
      }
      this.__otel_headers = headers

      span.addEvent('OPENED', { method, url })

      return self.originalOpen.apply(this, arguments)
    }

    // Patching XMLHttpRequest.send() to add tracing and traceparent headers
    XMLHttpRequest.prototype.send = function (body) {
      if (this.__otel_skip) {
        return self.originalSend.call(this, body)
      }
      const xhr = this
      const span = xhr.__otel_span
      let headers = xhr.__otel_headers

      for (const [key, value] of Object.entries(headers)) {
        try {
          xhr.setRequestHeader(key, value)
        } catch (e) {
          logger.warn('⚠️ Failed to set header:', key, e.message)
        }
      }

      xhr.addEventListener('readystatechange', () => {
        span.addEvent(`${self.stateLabels[xhr.readyState]}`, {
          readyState: xhr.readyState,
          state: self.stateLabels[xhr.readyState] || 'UNKNOWN'
        })
      })

      xhr.addEventListener('loadstart', () => {
        span.addEvent('REQUEST_START')
      })

      xhr.addEventListener('load', () => {
        span.setAttribute('http.status_code', xhr.status)
        span.addEvent('xhr:DONE')
        span.end()
        delete xhr.__otel_span
      })

      xhr.addEventListener('error', () => {
        span.setAttribute('error', true)
        span.addEvent('ERROR')
        span.end()
        delete xhr.__otel_span
      })

      xhr.addEventListener('abort', () => {
        span.setAttribute('http.aborted', true)
        span.addEvent('ABORTED')
        span.end()
        delete xhr.__otel_span
      })

      return self.originalSend.call(this, body)
    }
  }

  restore() {
    XMLHttpRequest.prototype.open = this.originalOpen
    XMLHttpRequest.prototype.send = this.originalSend
  }
}

const xhrTracer = new XHRTracer()
export { xhrTracer }
